'use client';
import { useEffect, useState } from 'react';

/**
 * Renders its children only once the browser is idle after the page loads, for widgets that do not need to be there
 * from the first paint (chat buttons, banners). Keeps them out of hydration, when the main thread is busiest.
 *
 * @param {React.ReactNode} children - What to render once idle.
 * @param {number} [timeout] - Longest wait in ms if the browser never gets idle. Defaults to 3000.
 * @return {React.ReactNode} The children once idle, nothing before.
 */
export const RenderWhenIdle = ({ children, timeout = 3000 }: { children: React.ReactNode; timeout?: number }) => {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    // Safari has no requestIdleCallback
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(() => setIdle(true), { timeout });
      return () => window.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(() => setIdle(true), Math.min(timeout, 1000));
    return () => window.clearTimeout(id);
  }, [timeout]);
  return idle ? children : null;
};
